import { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Button,
  Box,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { VolumeOff, VolumeUp, ArrowBack } from '@mui/icons-material';

import THEME from '../../constants/theme';
import {
  hasAcknowledgedRisk,
  setRiskAcknowledgment
} from '../../utils/riskUtils';
const introVideo =
  'https://www.dropbox.com/scl/fi/z2wmo97c9m3yhc0pgf42j/intro-video.mp4?rlkey=wrgplk2wixepz1rf4h9xbfklt&st=rbuuvnnk&raw=1';

/**
 * RiskGate component for displaying investment risk disclosure banner
 * SEO-friendly implementation that shows content to search engines
 * and allows user interaction while displaying risk banner
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} RiskGate component
 */
const RiskGate = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [showBanner, setShowBanner] = useState(false);
  const [showVideoScreen, setShowVideoScreen] = useState(false);
  const [isSearchEngine, setIsSearchEngine] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay compatibility
  const videoRef = useRef(null);

  /**
   * Detects if the current user agent is a search engine crawler
   * @returns {boolean} True if search engine detected
   */
  const detectSearchEngine = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const searchEnginePatterns = [
      'googlebot',
      'bingbot',
      'slurp',
      'duckduckbot',
      'baiduspider',
      'yandexbot',
      'facebookexternalhit',
      'twitterbot',
      'linkedinbot',
      'whatsapp',
      'telegrambot'
    ];

    return searchEnginePatterns.some(pattern => userAgent.includes(pattern));
  };

  useEffect(() => {
    const isBot = detectSearchEngine();
    const hasNoJavaScript = typeof window === 'undefined';
    const hasNoStorage = !window.localStorage;

    if (isBot || hasNoJavaScript || hasNoStorage) {
      setIsSearchEngine(true);
      return;
    }

    const hasAcknowledged = hasAcknowledgedRisk();
    if (!hasAcknowledged) {
      setTimeout(() => {
        setShowBanner(true);
      }, 1500); // Slightly longer delay to let page load completely
    }
  }, []);

  /**
   * Handles user acknowledgment of risk disclosure
   */
  const handleAcknowledge = () => {
    setRiskAcknowledgment(true);
    setShowBanner(false);
    setShowVideoScreen(false); // Also hide video screen if it was showing

    // Stop the video and reset audio when user accepts
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  /**
   * Handles user rejection of risk disclosure
   * Shows video screen instead of redirecting
   */
  const handleReject = () => {
    setRiskAcknowledgment(false);
    setShowBanner(false);
    setShowVideoScreen(true);

    // Enable sound when user actively chooses to watch video
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        setIsMuted(false);
        videoRef.current.play().catch(() => {
          // If unmuted autoplay fails, fallback to muted
          videoRef.current.muted = true;
          setIsMuted(true);
          videoRef.current.play();
        });
      }
    }, 100);
  };

  /**
   * Reopens the risk banner from video screen
   */
  const handleReopenBanner = () => {
    setShowVideoScreen(false);
    setShowBanner(true);

    // Stop the video and reset audio when returning to banner
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
      setIsMuted(true);
    }
  };

  /**
   * Toggles video mute state
   */
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  return (
    <>
      {children}

      {!isSearchEngine && (
        <>
          {/* Risk Disclosure Banner */}
          {showBanner && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(248, 250, 252, 0.98)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                p: {
                  xs: '6px 12px',
                  md: '20px 16px 20px 16px'
                },
                pr: { xs: '12px', md: 3 },
                px: { md: 3 },
                zIndex: 10000, // Higher than navigation menu (9999)
                borderBottom: `3px solid ${THEME.COLORS.PRIMARY_BLUE}`,
                backdropFilter: 'blur(10px)' // Add blur effect for better contrast
              }}
            >
              <Box
                sx={{
                  maxWidth: '1600px',
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'center', md: 'center' },
                  gap: { xs: 1, md: 1 },
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant={isMobile ? 'body2' : 'h5'}
                    sx={{
                      mb: { xs: 0.25, md: 1 },
                      mt: { xs: 0.25, md: 1 },
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                      color: THEME.COLORS.NEUTRAL_BLACK,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontSize: { xs: '0.875rem', md: '1.5rem' }
                    }}
                  >
                    Important Risk Disclosure
                  </Typography>
                  <Typography
                    variant={isMobile ? 'body2' : 'body1'}
                    color='text.secondary'
                    sx={{
                      lineHeight: { xs: 1.2, md: 1.5 },
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      mb: { xs: 0.25, md: 0 },
                      fontSize: { xs: '0.7rem', md: '1rem' },
                      display: { xs: '-webkit-box', md: 'block' },
                      WebkitLineClamp: { xs: 3, md: 'none' },
                      WebkitBoxOrient: { xs: 'vertical', md: 'initial' },
                      overflow: { xs: 'hidden', md: 'visible' }
                    }}
                  >
                    This website is intended solely for professional and
                    institutional investors as defined under applicable
                    regulations. Investments in alternative strategies involve
                    significant risk, including the possible loss of the entire
                    amount invested. Past performance is not indicative of
                    future results. Nothing contained herein constitutes
                    investment advice, an offer, or a solicitation.
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, sm: 2 },
                    alignItems: 'center',
                    minWidth: { xs: 'auto', md: '280px' },
                    flexShrink: 0,
                    width: { xs: '100%', md: 'auto' },
                    justifyContent: { xs: 'center', md: 'flex-end' }
                  }}
                >
                  <Button
                    variant='outlined'
                    onClick={handleReject}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      whiteSpace: 'nowrap',
                      color: THEME.COLORS.NEUTRAL_BLACK,
                      borderColor: THEME.COLORS.NEUTRAL_GRAY,
                      textTransform: 'none',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                      fontSize: { xs: '0.7rem', md: '0.875rem' },
                      py: { xs: '4px', md: '8px' },
                      px: { xs: '10px', md: '16px' },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        borderColor: THEME.COLORS.NEUTRAL_BLACK
                      }
                    }}
                  >
                    Decline
                  </Button>
                  <Button
                    variant='contained'
                    onClick={handleAcknowledge}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      whiteSpace: 'nowrap',
                      backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                      color: THEME.COLORS.NEUTRAL_WHITE,
                      textTransform: 'none',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                      fontSize: { xs: '0.7rem', md: '0.875rem' },
                      py: { xs: '4px', md: '8px' },
                      px: { xs: '10px', md: '16px' },
                      '&:hover': {
                        backgroundColor: THEME.COLORS.PRIMARY_BLUE_DARK
                      },
                      boxShadow: '0 2px 4px rgba(0, 103, 218, 0.15)'
                    }}
                  >
                    Accept & Continue
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          <Fade in={showVideoScreen} timeout={500}>
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 20000,
                backgroundColor: 'black'
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted={isMuted}
                loop
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              >
                <source src={introVideo} type='video/mp4' />
              </video>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: 30,
                  right: 30,
                  display: 'flex',
                  gap: 2
                }}
              >
                <IconButton
                  onClick={toggleMute}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'black',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 1)'
                    }
                  }}
                >
                  {isMuted ? <VolumeOff /> : <VolumeUp />}
                </IconButton>

                <Button
                  variant='contained'
                  startIcon={<ArrowBack />}
                  onClick={handleReopenBanner}
                  sx={{
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                    color: THEME.COLORS.NEUTRAL_WHITE,
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                    '&:hover': {
                      backgroundColor: THEME.COLORS.PRIMARY_BLUE_DARK
                    }
                  }}
                >
                  Back to Website
                </Button>
              </Box>
            </Box>
          </Fade>
        </>
      )}
    </>
  );
};

export default RiskGate;
